import cx from 'classnames';
import {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, Icon} from 'components';
import {TRANSLATION_KEYS} from 'locale';
import {ComplianceCheckResult} from 'types';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.SESSION_TEXT_CHAT.COMPLIANCE;

type Props = {
    result: ComplianceCheckResult;
    originalContent: string;
    className?: string;
    onAccept: () => void;
    onIgnore: () => void;
    onDismiss: () => void;
}

export const CompliancePanel: React.FC<Props> = ({result, originalContent, className, onAccept, onIgnore, onDismiss}) => {
    const {t} = useTranslation();
    const [detailsOpen, setDetailsOpen] = useState(false);
    const toggleDetails = useCallback(() => setDetailsOpen((value) => !value), []);

    return (
        <div
            className={cx(
                'tw-bg-light tw-border tw-rounded-xl tw-p-3 tw-text-base tw-text-dark tw-break-words',
                'tw-flex tw-flex-col tw-gap-2', className
            )}
            style={{borderColor: 'rgba(var(--color-warning), 1)'}}
        >
            <div className='tw-flex tw-items-center tw-text-warning tw-text-base'>
                <Icon name='alert-triangle' color='warning' size='sm' margin={false} className='tw-shrink-0 tw-mr-2' />
                <span className='tw-font-bold tw-grow'>
                    {t(T_KEYS.TITLE)}
                </span>
                <Icon name='close' button size='sm' margin={false} className='tw-shrink-0 tw-ml-2' onClick={onDismiss} />
            </div>
            {result.rule && <div className='tw-font-semibold tw-text-dark tw-text-base'>{result.rule}</div>}
            {result.suggestedRewording &&
                <div>
                    <div className='tw-text-default-dimmed tw-text-sm'>{t(T_KEYS.SUGGESTION_LABEL)}</div>
                    <div className='tw-mt-1 tw-p-2 tw-bg-primary-lighten tw-bg-opacity-25 tw-border tw-border-default tw-rounded-md'>
                        {result.suggestedRewording}
                    </div>
                </div>}
            <Button
                color='unstyled'
                width='auto'
                height='auto'
                fontStyle='normal'
                className='tw-self-start tw-mt-1 tw-text-default-dimmed !tw-border-0 !tw-p-0'
                aria-expanded={detailsOpen}
                Icon={<Icon name={detailsOpen ? 'chevron-down' : 'chevron-right'} size='sm' margin={false} />}
                onClick={toggleDetails}
            >
                {t(T_KEYS.WHY_FLAGGED)}
            </Button>
            {detailsOpen &&
                <div className='-tw-mt-1 tw-flex tw-flex-col tw-gap-2'>
                    {result.reason && <div className='tw-text-dark'>{result.reason}</div>}
                    <div>
                        <div className='tw-text-default-dimmed tw-text-sm'>{t(T_KEYS.ORIGINAL_LABEL)}</div>
                        <div className='tw-mt-1 tw-p-2 tw-bg-layout tw-border tw-border-default tw-rounded-md'>
                            {originalContent}
                        </div>
                    </div>
                </div>}
            <div className='tw-mt-2 tw-flex tw-flex-col tw-gap-2'>
                {result.suggestedRewording &&
                    <Button color='primary' width='full' icon='send' className='tw-rounded-md' onClick={onAccept}>
                        {t(T_KEYS.ACCEPT)}
                    </Button>}
                <Button
                    color='unstyled'
                    width='full'
                    height='auto'
                    fontStyle='normal'
                    className='tw-text-default-dimmed !tw-border-0 !tw-p-0'
                    onClick={onIgnore}
                >
                    {t(T_KEYS.IGNORE)}
                </Button>
            </div>
        </div>
    );
};
